import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme
} from "@mui/material/";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { groupBy } from "lodash";
import actions from "../../Store/actions";
import { strings } from "../../common/i18n";
import { logToSentry } from "../../config/Sentry";

const COLS = [
    { key: "row",              label: (s) => s("row"),              mono: true,  width: null },
    { key: "branch",           label: (s) => s("branch"),           mono: false, width: 120 },
    { key: "assetIdentifier",  label: (s) => s("assetIdentifier"),  mono: false, width: null },
    { key: "assetIdentifier2", label: (s) => s("identifier2"),      mono: false, width: null },
    { key: "branchReference",  label: (s) => s("ref"),              mono: false, width: 110 },
];

const ValidationErrorDetailPanel = ({ uploadId }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            try {
                const result = await dispatch(
                    actions.validationErrors.create.getRequest(uploadId)
                );
                setErrors(result?.validationErrors ?? []);
            } catch (error) {
                logToSentry("ValidationErrorDetailPanel fetch:", { error });
                setErrors([]);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [uploadId]);

    const grouped = useMemo(
        () => groupBy(errors, (e) => e.error || "Unknown error"),
        [errors]
    );

    const sorted = useMemo(
        () => Object.entries(grouped).sort(([, a], [, b]) => a.length - b.length),
        [grouped]
    );

    const totalErrors = errors.length;
    const groupCount  = sorted.length;

    // ── Loading ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Box sx={{ px: 3, py: 2 }}>
                {[1, 2].map((i) => (
                    <Skeleton
                        key={i}
                        variant="rounded"
                        height={40}
                        sx={{ mb: 1, borderRadius: 1 }}
                        animation="wave"
                    />
                ))}
            </Box>
        );
    }

    // ── Empty state ───────────────────────────────────────────────────────────
    if (!errors.length) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 3,
                    py: 2
                }}
            >
                <CheckCircleOutlineIcon
                    fontSize="small"
                    sx={{ color: "success.main", flexShrink: 0 }}
                />
                <Typography variant="body2" color="text.secondary">
                    {strings("messages.noValidationErrors")}
                </Typography>
            </Box>
        );
    }

    // ── Error list ────────────────────────────────────────────────────────────
    return (
        <Box sx={{ px: 2.5, pt: 1.5, pb: 2 }}>

            {/* Summary bar */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    mb: 1.5,
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderLeft: `3px solid ${theme.palette.error.main}`,
                    overflow: "hidden"
                }}
            >
                {/* Icon */}
                <Box sx={{ display: "flex", alignItems: "center", px: 1.5, flexShrink: 0 }}>
                    <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: "error.main" }} />
                </Box>

                {/* Divider */}
                <Box sx={{ width: "1px", height: 36, backgroundColor: theme.palette.divider, flexShrink: 0 }} />

                {/* Stat: total issues */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.75,
                        px: 2,
                        py: 0.875
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.95rem",
                            fontWeight: 700,
                            lineHeight: 1,
                            color: theme.palette.error.dark,
                            fontFamily: theme.typography.fontFamily
                        }}
                    >
                        {totalErrors}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "0.68rem",
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            color: theme.palette.text.secondary,
                            fontFamily: theme.typography.fontFamily
                        }}
                    >
                        {totalErrors === 1 ? "Issue" : "Issues"}
                    </Typography>
                </Box>

                {/* Divider */}
                <Box sx={{ width: "1px", height: 36, backgroundColor: theme.palette.divider, flexShrink: 0 }} />

                {/* Stat: error types */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.75,
                        px: 2,
                        py: 0.875
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.95rem",
                            fontWeight: 700,
                            lineHeight: 1,
                            color: theme.palette.error.dark,
                            fontFamily: theme.typography.fontFamily
                        }}
                    >
                        {groupCount}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "0.68rem",
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            color: theme.palette.text.secondary,
                            fontFamily: theme.typography.fontFamily
                        }}
                    >
                        {groupCount === 1 ? "Error Type" : "Error Types"}
                    </Typography>
                </Box>
            </Box>

            {/* Accordion per error group */}
            {sorted.map(([errorMsg, rows], groupIdx) => (
                <Accordion
                    key={errorMsg}
                    disableGutters
                    elevation={0}
                    defaultExpanded={groupCount === 1}
                    sx={{
                        mb: 0.75,
                        borderRadius: "6px !important",
                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        borderLeft: `3px solid ${theme.palette.error.main}`,
                        overflow: "hidden",
                        "&:before": { display: "none" },
                        "&.Mui-expanded": {
                            borderColor: alpha(theme.palette.error.main, 0.35),
                            borderLeft: `3px solid ${theme.palette.error.dark}`
                        }
                    }}
                >
                    <AccordionSummary
                        expandIcon={
                            <ExpandMoreIcon
                                sx={{ fontSize: 18, color: "error.main" }}
                            />
                        }
                        sx={{
                            minHeight: 38,
                            px: 1.5,
                            backgroundColor: theme.palette.background.paper,
                            "&:hover": {
                                backgroundColor: theme.palette.action.hover
                            },
                            "& .MuiAccordionSummary-content": { my: 0, alignItems: "center", gap: 1 }
                        }}
                    >
                        <ErrorOutlineIcon
                            sx={{ fontSize: 14, color: "error.main", flexShrink: 0 }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: "error.dark",
                                fontFamily: theme.typography.fontFamily,
                                textTransform: "capitalize",
                                flex: 1,
                                lineHeight: 1.4
                            }}
                        >
                            {errorMsg}
                        </Typography>
                        <Chip
                            label={`${rows.length} ${rows.length === 1 ? "row" : "rows"}`}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                backgroundColor: alpha(theme.palette.error.main, 0.12),
                                color: "error.dark",
                                border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                                "& .MuiChip-label": { px: 1 }
                            }}
                        />
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0 }}>
                        <Table
                            size="small"
                            sx={{ tableLayout: "fixed" }}
                        >
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: alpha(
                                            theme.palette.text.primary,
                                            0.03
                                        )
                                    }}
                                >
                                    {COLS.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            sx={{
                                                width: col.width ?? undefined,
                                                py: 1,
                                                px: 1.5,
                                                fontSize: "0.7rem",
                                                fontWeight: 700,
                                                fontFamily: theme.typography.fontFamily,
                                                letterSpacing: 0.4,
                                                textTransform: "uppercase",
                                                color: theme.palette.text.secondary,
                                                borderBottom: `1px solid ${alpha(
                                                    theme.palette.error.main,
                                                    0.15
                                                )}`
                                            }}
                                        >
                                            {col.label(strings)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow
                                        key={i}
                                        sx={{
                                            backgroundColor:
                                                i % 2 === 0
                                                    ? "transparent"
                                                    : alpha(theme.palette.text.primary, 0.02),
                                            "&:last-child td": { borderBottom: 0 },
                                            "&:hover": {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        {/* Row number — monospace badge */}
                                        <TableCell
                                            sx={{
                                                py: 1,
                                                px: 1.5,
                                                width: "auto",
                                                whiteSpace: "nowrap",
                                                borderBottom: `1px solid ${alpha(
                                                    theme.palette.divider,
                                                    0.6
                                                )}`
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "inline-block",
                                                    fontFamily: theme.typography.fontFamily,
                                                    fontSize: "0.72rem",
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                    color: theme.palette.text.secondary,
                                                    backgroundColor: alpha(
                                                        theme.palette.text.primary,
                                                        0.06
                                                    ),
                                                    borderRadius: "3px",
                                                    px: 0.6,
                                                    py: 0.1
                                                }}
                                            >
                                                {row.row ?? "—"}
                                            </Box>
                                        </TableCell>

                                        {/* Data cells */}
                                        {[
                                            row.branch,
                                            row.assetIdentifier,
                                            row.assetIdentifier2,
                                            row.branchReference
                                        ].map((val, ci) => (
                                            <TableCell
                                                key={ci}
                                                sx={{
                                                    py: 1,
                                                    px: 1.5,
                                                    fontSize: "0.78rem",
                                                    fontFamily: theme.typography.fontFamily,
                                                    color: val
                                                        ? theme.palette.text.primary
                                                        : theme.palette.text.disabled,
                                                    borderBottom: `1px solid ${alpha(
                                                        theme.palette.divider,
                                                        0.6
                                                    )}`
                                                }}
                                            >
                                                {val || "—"}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default ValidationErrorDetailPanel;
